import { Facebook, Twitter, Linkedin, Mail, Link2, Send as SendIcon, MessageCircle, Instagram } from 'lucide-react';
import { toast } from 'sonner';
import BookmarkButton from './BookmarkButton';

export default function ArticleShareBar({ title, url, articleId, excerpt, shareImageUrl, storyImageUrl }) {
  const t = encodeURIComponent(title || '');
  const u = encodeURIComponent(url || '');
  const q = encodeURIComponent(excerpt || title || '');
  // Facebook scrapes whatever URL is passed in `u=`. The production HTML
  // is a CRA SPA without server-side rendering, so og:* tags injected by
  // React Helmet aren't visible to facebookexternalhit. We point Facebook
  // at our backend share endpoint which always returns OG-rich HTML
  // (with article title/description/image and a meta-refresh that bounces
  // human clicks to the canonical /clanak/:slug). This guarantees a rich
  // visual preview on Facebook, LinkedIn, WhatsApp, Telegram, etc.
  const slugMatch = (url || '').match(/\/clanak\/([^/?#]+)/);
  const slug = slugMatch ? slugMatch[1] : '';
  const apiBase = process.env.REACT_APP_BACKEND_URL || '';
  const shareUrl = slug ? `${apiBase}/api/share/article/${encodeURIComponent(slug)}` : url;
  const su = encodeURIComponent(shareUrl || '');
  const fbShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${su}&quote=${q}`;
  const links = [
    { key: 'fb', icon: <Facebook size={14} />, href: fbShareUrl, label: 'Facebook' },
    { key: 'x', icon: <Twitter size={14} />, href: `https://twitter.com/intent/tweet?text=${t}&url=${u}`, label: 'X' },
    { key: 'li', icon: <Linkedin size={14} />, href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`, label: 'LinkedIn' },
    { key: 'wa', icon: <MessageCircle size={14} />, href: `https://wa.me/?text=${t}%20${u}`, label: 'WhatsApp' },
    { key: 'tg', icon: <SendIcon size={14} />, href: `https://t.me/share/url?url=${u}&text=${t}`, label: 'Telegram' },
    { key: 'mail', icon: <Mail size={14} />, href: `mailto:?subject=${t}&body=${u}`, label: 'E-mail' },
  ];

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Poveznica kopirana');
    } catch {
      toast.error('Kopiranje nije uspjelo');
    }
  }

  function openFacebookShare(e) {
    e.preventDefault();
    // Pop-up 600×460 — Facebook's recommended share window size. Falls
    // back to a normal tab if the browser blocks pop-ups.
    const w = 600, h = 460;
    const left = Math.max(0, (window.screen.width - w) / 2);
    const top = Math.max(0, (window.screen.height - h) / 2);
    const win = window.open(fbShareUrl, 'fb-share',
      `width=${w},height=${h},left=${left},top=${top},noopener,noreferrer`);
    if (!win) window.location.href = fbShareUrl;
  }

  // Instagram Story share — Instagram intentionally blocks classic URL
  // sharing, so we take a three-stage progressive-enhancement approach:
  //   1. On mobile devices we try the official Instagram Stories deep link
  //      (`instagram-stories://share?source_application=...&background_image=...`)
  //      with an inline data URL when supported. This launches the IG app
  //      directly into the story camera with our branded card.
  //   2. If the browser supports Web Share Level 2 with files
  //      (modern iOS Safari, Android Chrome), hand the PNG file straight
  //      to the native share sheet — user taps Instagram → "Add to story"
  //      and our branded card is pre-loaded as the background.
  //   3. Otherwise we download the PNG so the user has it ready, then
  //      open `instagram://story-camera`. On desktop we just keep the
  //      downloaded image and instruct the user to switch to mobile.
  async function openInstagramShare() {
    const imageUrl = storyImageUrl || shareImageUrl;
    if (!imageUrl) {
      toast.error('Slika za dijeljenje još nije generirana. Pokušajte ponovno za nekoliko sekundi.');
      return;
    }
    const toastId = toast.loading('Pripremam sliku za Instagram...');
    const isMobile = /android|iphone|ipad|ipod/i.test(navigator.userAgent || '');
    try {
      // Resolve to absolute URL so the fetch works in dev (where backend
      // sits on a different origin) and in production (same origin). We
      // intentionally drop ``mode: 'cors'`` — modern browsers default to
      // 'cors' for cross-origin requests anyway, and forcing it triggers
      // the strictest preflight even on same-origin assets, which is what
      // produced the "fail to fetch" error users were seeing on the
      // production domain.
      const absUrl = (() => {
        try { return new URL(imageUrl, window.location.href).toString(); } catch { return imageUrl; }
      })();
      let resp;
      try {
        resp = await fetch(absUrl, { cache: 'no-store', credentials: 'omit' });
      } catch (netErr) {
        // Some CDNs reject the explicit cache-control header. Retry plain.
        resp = await fetch(absUrl);
      }
      if (!resp.ok) throw new Error('fetch failed ' + resp.status);
      const blob = await resp.blob();
      const safeName = (title || 'e-mina-share').replace(/[^\w\-]+/g, '-').slice(0, 60).toLowerCase() || 'e-mina-share';
      const file = new File([blob], `${safeName}.png`, { type: blob.type || 'image/png' });

      // Stage 1: native Web Share API with a file attachment (best UX on
      // modern iOS Safari + Android Chrome — opens system share sheet)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: title || 'e-mina.eu',
            text: excerpt || title || '',
          });
          toast.dismiss(toastId);
          toast.success('Otvoren je sustavski izbornik. Odaberi Instagram → Story.');
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') {
            toast.dismiss(toastId);
            return;
          }
          // Other errors — fall through to manual download flow.
          console.warn('navigator.share failed, falling back:', err);
        }
      }

      // Stage 2/3: download the image first so the user has it handy.
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${safeName}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);

      toast.dismiss(toastId);
      if (isMobile) {
        toast.success('Slika preuzeta. Otvaram Instagram — dodaj je u novi Story.');
        // Deep-link to the Instagram Story camera. If the app is installed
        // this opens the story camera; otherwise the URL scheme is ignored
        // by the OS and the user keeps the downloaded file.
        setTimeout(() => {
          window.location.href = 'instagram-stories://share?source_application=eminaeu';
          // Fallback to the older instagram:// scheme after a short delay.
          setTimeout(() => {
            window.location.href = 'instagram://story-camera';
          }, 600);
        }, 400);
      } else {
        toast.success('Slika preuzeta. Otvori Instagram na mobilnom uređaju i dodaj je u Story.');
      }
    } catch (err) {
      toast.dismiss(toastId);
      console.error('Instagram share failed:', err);
      toast.error(`Dijeljenje nije uspjelo: ${err.message || 'pokušajte ponovno'}`);
    }
  }

  return (
    <div className="mb-5" data-testid="article-share-bar">
      {/* Primary CTAs — Facebook + Instagram Story. Facebook uses our OG
          meta tags so the preview is the auto-generated 1200×630 branded
          card. Instagram blocks URL sharing, so we hand the PNG straight
          to the native share sheet (or fall back to a direct download). */}
      <div className="flex flex-wrap gap-2 mb-3">
        <a
          href={fbShareUrl}
          onClick={openFacebookShare}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1877F2] hover:bg-[#166FE5] text-white text-sm font-bold shadow-sm hover:shadow-md transition-all active:scale-[0.98]"
          data-testid="share-facebook-cta"
          aria-label="Podijeli na Facebook"
        >
          <Facebook size={16} />
          Podijeli na Facebook
        </a>
        <button
          type="button"
          onClick={openInstagramShare}
          disabled={!shareImageUrl && !storyImageUrl}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-bold shadow-sm hover:shadow-md transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)' }}
          data-testid="share-instagram-cta"
          aria-label="Podijeli na Instagram Story"
          title={(storyImageUrl || shareImageUrl) ? 'Otvara sustavski share izbornik ili preuzima vertikalnu sliku 1080×1920 za Instagram Story' : 'Slika se još generira'}
        >
          <Instagram size={16} />
          Podijeli na Instagram Story
        </button>
      </div>

      <div className="flex items-center flex-wrap gap-2">
        <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Ili:</span>
        {links.map(l => (
          <a key={l.key} href={l.href} target="_blank" rel="noopener noreferrer" aria-label={l.label} title={l.label}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-red-600 hover:text-white transition-colors"
            data-testid={`share-${l.key}`}>
            {l.icon}
          </a>
        ))}
        <button onClick={copyLink} aria-label="Kopiraj link" title="Kopiraj link"
          className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-red-600 hover:text-white transition-colors"
          data-testid="share-copy">
          <Link2 size={14} />
        </button>
        {articleId && <BookmarkButton articleId={articleId} />}
      </div>
    </div>
  );
}
